import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IconBackspace } from '@tabler/icons-react';
import { Button } from '../components/ui';
import CodedPill from '../components/CodedPill';
import { useAuth } from '../hooks/useAuth';

/**
 * Login — 4-digit PIN entry.
 *
 * Phase 1.2c: wired to real /api/auth/login/pin. The branch is held
 * in the device's local storage so a station "knows" what branch it
 * lives in (currently a sensible default — admins will be able to
 * change it in Phase 1.3 station-setup UI).
 *
 * Bounces back to the originally-requested route after success.
 */

const DEFAULT_BRANCH_ID = 'HL01'; // matches bootstrap default; future: per-station
const BRANCH_STORAGE_KEY = 'pos.station.branchId';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signInWithPin, isAuthenticated, loading } = useAuth();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: string })?.from || '/pos';

  // If already signed in (e.g. came here directly while session valid),
  // bounce away.
  useEffect(() => {
    if (!loading && isAuthenticated) navigate(from, { replace: true });
  }, [loading, isAuthenticated, from, navigate]);

  const append = (d: string) => {
    if (submitting) return;
    setError(null);
    setPin((p) => (p.length < 4 ? p + d : p));
  };
  const back = () => setPin((p) => p.slice(0, -1));
  const clear = () => { setPin(''); setError(null); };

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length !== 4 || submitting) return;
    let cancelled = false;
    (async () => {
      setSubmitting(true);
      try {
        const branchId =
          localStorage.getItem(BRANCH_STORAGE_KEY) ?? DEFAULT_BRANCH_ID;
        await signInWithPin(branchId, pin);
        if (!cancelled) navigate(from, { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message || 'Incorrect PIN');
          setPin('');
        }
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="min-h-screen bg-light flex flex-col">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm flex flex-col items-center">
          {/* Brand */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img
                src="/icons/icon-96.png"
                alt="CODED"
                className="w-11 h-11 rounded-xl flex-shrink-0 shadow-sm"
              />
              <h1 className="text-22 font-bold text-heading tracking-tight leading-none">
                CODED. POS
              </h1>
            </div>
            <p className="text-13 text-body">HL Coffee Shop · Point of Sale</p>
          </div>

          {/* PIN dots */}
          <div className="flex items-center gap-3 mb-10" aria-label="PIN entry">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className={[
                  'w-3 h-3 rounded-pill transition-all',
                  i < pin.length
                    ? 'bg-primary scale-100'
                    : 'bg-transparent border border-border scale-90',
                ].join(' ')}
              />
            ))}
          </div>

          {error && (
            <p className="text-sm text-danger mb-6 -mt-4 font-medium" role="alert">
              {error}
            </p>
          )}

          {/* Numeric keypad */}
          <div className="grid grid-cols-3 gap-3 w-full">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <Button
                key={n}
                variant="light"
                size="pos"
                fullWidth
                onClick={() => append(String(n))}
                disabled={submitting}
                className="text-22"
              >
                {n}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="pos"
              fullWidth
              onClick={clear}
              disabled={submitting}
              className="text-12 uppercase tracking-wider"
            >
              Clear
            </Button>
            <Button
              variant="light"
              size="pos"
              fullWidth
              onClick={() => append('0')}
              disabled={submitting}
              className="text-22"
            >
              0
            </Button>
            <Button
              variant="ghost"
              size="pos"
              fullWidth
              onClick={back}
              disabled={submitting}
              leadingIcon={<IconBackspace size={20} stroke={1.75} />}
            >
              {''}
            </Button>
          </div>

          <p className="text-sm text-ink-tertiary mt-8">
            {submitting ? 'Signing in…' : 'Enter your 4-digit PIN to begin'}
          </p>
        </div>
      </main>

      <footer className="pb-8 pt-4 flex flex-col items-center gap-2.5">
        <span className="text-xs text-ink-tertiary">This platform runs on the</span>
        <CodedPill size="sm" />
      </footer>
    </div>
  );
}
